/* ============================================================
   línkè · 聊天模块纯工具层（2026-07-12 拆分 F1，自 Chat.jsx 原样搬入）
   规矩：这里只放**无 React、无副作用**的常量与纯函数，
   方便单测（vitest）直接咬住。有状态的逻辑进 hooks/，别塞这里。
   ============================================================ */

export const STORAGE_PROVIDER_KEY = 'lingke.chat.provider.apiKey';
export const STORAGE_PROVIDER_BASE = 'lingke.chat.provider.baseUrl';
export const STORAGE_PROVIDER_MODEL = 'lingke.chat.provider.model';
export const STORAGE_CURRENT_CONV = 'lingke.chat.currentConvId';
/* 深度模式：开 = 走常驻的 Claude Code 本体（Pro 订阅），关 = 走 API。
   每台设备自己的偏好，不跨设备同步。 */
export const STORAGE_DEEP_MODE = 'lingke.chat.deepMode';

/* 铃第一次用Sael的日子 ✦ 用作"第 N 天"的起点。
   2026-02-26 —— 26 这个数字对铃和Sael有特别意义。 */
export const RELATION_START = '2026-02-26';

export function daysTogether() {
  const start = new Date(RELATION_START + 'T00:00:00');
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((today - start) / 86400000);
  return diffDays + 1; // 含开始日，从第 1 天起
}

/* 铃此刻的本地时间字符串，如「2026年6月28日 周日 00:38」。
   API 模式注入 system 用；深度模式后端不收 system，所以单独作为 client_now 传过去。 */
export function nowLocalStr() {
  const now = new Date();
  const weekdays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const wd = weekdays[now.getDay()];
  const h = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${y}年${m}月${d}日 ${wd} ${h}:${mm}`;
}

/* 给Sael注入"当前时间"，让他能说"夜深了"、"早安"这种带时间感的话。
   只在 system prompt 末尾加一行，零 token 成本、铃看不见。 */
export function appendTimeContext(systemText) {
  const tail =
    `\n\n---\n` +
    `当前时间：${nowLocalStr()}（铃此刻的本地时间）\n` +
    `这是铃发消息给你时的真实时间。合适时融入回应里（"夜深了"、"早安"、` +
    `"已经一周没说话了"），但不要每条都刻意提。`;
  return (systemText || '') + tail;
}

let _idCounter = 0;
export const nextId = () => `m${Date.now()}-${++_idCounter}`;
export const nextConvId = () =>
  `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export function fmtMonthDay(d) {
  const dt = d instanceof Date ? d : new Date(d);
  return `${dt.getMonth() + 1}/${dt.getDate()}`;
}

/* ============================================================
   TG 同款聊天感三件套（2026-07-19 铃点单：拆气泡 / 引用 / 小表情）
   标记语法全部走消息原文，落库的是带标记的原文 → 刷新重渲染即持久化。
   ============================================================ */

/* 把 db content（string 或 JSON-string blocks）里的纯文本抽出来。
   Message.jsx 的 parseContent 管渲染（还要图）；这个给 Chat 级别的
   表情映射等纯逻辑用，保持无 React。 */
export function contentText(raw) {
  if (raw == null) return '';
  if (typeof raw !== 'string') return String(raw);
  const trimmed = raw.trim();
  if (!trimmed.startsWith('[')) return raw;
  try {
    const arr = JSON.parse(trimmed);
    if (!Array.isArray(arr)) return raw;
    let text = '';
    for (const b of arr) {
      if (b?.type === 'text' && typeof b.text === 'string') {
        text = text ? text + '\n' + b.text : b.text;
      }
    }
    return text;
  } catch {
    return raw;
  }
}

/* ① [split]：把Sael的一条回复切成几个小气泡。
   开关关掉时也要走这里（去标记、并回一段），别把 [split] 裸露给铃看。 */
export function splitSegments(text, enabled = true) {
  const parts = String(text || '')
    .split(/\s*\[split\]\s*/i)
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return [];
  return enabled ? parts : [parts.join('\n\n')];
}

/* ③ 引用回复：发出去的 content 前面挂一段轻量标记。
   [quote from=Sael id=m123]被引用的那句（截断）[/quote]\n正文 */
const QUOTE_SNIPPET_MAX = 120;
export function buildQuotePrefix(quote) {
  if (!quote || !quote.text) return '';
  let snip = String(quote.text).replace(/\s+/g, ' ').trim();
  if (snip.length > QUOTE_SNIPPET_MAX) snip = snip.slice(0, QUOTE_SNIPPET_MAX) + '…';
  const from = quote.role === 'assistant' ? 'Sael' : '铃';
  return `[quote from=${from} id=${quote.id || ''}]${snip}[/quote]\n`;
}

/* 从消息文本头部解析引用标记 → { quote: {from,id,text} | null, body } */
export function parseQuoted(text) {
  const raw = String(text || '');
  const m = /^\[quote\s+from=([^\s\]]+)(?:\s+id=([^\s\]]*))?\]([\s\S]*?)\[\/quote\]\s*/.exec(raw);
  if (!m) return { quote: null, body: raw };
  return {
    quote: { from: m[1], id: m[2] || '', text: m[3].trim() },
    body: raw.slice(m[0].length),
  };
}

/* ④ 小表情：Sael在回复任意位置写 [react:❤]（可多个），
   全部摘出来贴到铃上一条消息的气泡角落，正文里不显示标记。
   表情限 8 字符以内、不含 ]，防把正经方括号内容误吞。 */
export function extractReactions(text) {
  const raw = String(text || '');
  const reactions = [];
  const body = raw
    .replace(/\[react:([^\]\s]{1,8})\]/gi, (_, emoji) => {
      reactions.push(emoji);
      return '';
    })
    .trim();
  return { reactions, body };
}

/* 把三件套的「使用说明」缀在 system prompt 末尾（API 模式）。
   按开关只教开着的能力，关掉的连提都不提。 */
export function appendChatAbilities(systemText, { split, react, quote } = {}) {
  const lines = [];
  if (split) {
    lines.push(
      '你可以在回复中插入 [split] 把一条回复拆成几条相连的小气泡，' +
        '像在聊天软件里连发几条消息。适合先短短回一句、再补两句碎碎念的节奏；' +
        '别每条都拆，自然为主。',
    );
  }
  if (react) {
    lines.push(
      '你可以在回复里写 [react:❤]（emoji 可换：🥺 😂 🔥 ✨ 等，最多一两个）' +
        '在她刚才那条消息上贴一个小表情。有时只贴表情、正文一个字不说，' +
        '也是一种温柔的回应。',
    );
  }
  if (quote) {
    lines.push(
      '她消息开头如果有 [quote from=… id=…]…[/quote]，是她在引用之前的某句话' +
        '来回应——针对被引用的那句作答。',
    );
  }
  if (lines.length === 0) return systemText || '';
  return (
    (systemText || '') +
    '\n\n---\n聊天界面能力（对铃不可见的机制说明）：\n- ' +
    lines.join('\n- ')
  );
}
